// src/components/project/ProjectUsersModal.jsx
import { useState, useMemo, memo, useRef, useEffect } from 'react';

const ProjectUsersModal = memo(({ 
  project, 
  allUsers, 
  onClose, 
  onAddUser, 
  onRemoveUser 
}) => {
  const [selectedUser, setSelectedUser] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const modalRef = useRef(null);

  // Закрытие при клике вне модального окна
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Мемоизация фильтрации пользователей
  const filteredUsers = useMemo(() => {
    if (!allUsers) return [];
    
    return allUsers.filter(u => {
      const fullName = `${u.surname || ''} ${u.name || ''}`.toLowerCase();
      const username = u.username.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase()) || 
             username.includes(searchTerm.toLowerCase());
    });
  }, [allUsers, searchTerm]);

  const usersInProject = useMemo(() => project.users || [], [project.users]);

  // Мемоизация пользователей, которых можно добавить
  const availableUsers = useMemo(() => {
    return filteredUsers.filter(u => 
      !usersInProject.some(p => p.id === u.id)
    );
  }, [filteredUsers, usersInProject]);

  const handleAddUser = () => {
    if (selectedUser) {
      onAddUser(project.id, Number(selectedUser));
      setSelectedUser('');
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  // Мемоизация модального окна
  const modalContent = useMemo(() => {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" ref={modalRef} style={{ maxWidth: '800px' }} onClick={handleModalClick}>
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Пользователи проекта: {project.name_text}</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
            />
          </div>

          <div className="modal-body">
            {/* Список текущих пользователей */}
            <div className="mb-4">
              <h6>Текущие пользователи ({usersInProject.length})</h6>
              {usersInProject.length > 0 ? (
                <div className="row g-3">
                  {usersInProject.map((u) => (
                    <div key={u.id} className="col-md-6 col-lg-4">
                      <div className="card border-primary">
                        <div className="card-body">
                          <h6 className="card-title">
                            {u.surname && u.name 
                              ? `${u.name} ${u.surname}` 
                              : u.username}
                          </h6>
                          <p className="card-text text-muted mb-2">
                            {u.username}
                          </p>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => onRemoveUser(project.id, u.id)}
                          >
                            Удалить
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="alert alert-info">
                  В проекте нет пользователей
                </div>
              )}
            </div>

            <hr />

            {/* Форма добавления пользователя */}
            <div>
              <h6>Добавить пользователя</h6>
              <div className="input-group mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Поиск пользователя..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddUser}
                  disabled={!selectedUser}
                >
                  Добавить
                </button>
              </div>

              {availableUsers.length === 0 ? (
                <div className="alert alert-warning">
                  {searchTerm ? 'Нет пользователей по запросу' : 'Все пользователи уже в проекте'}
                </div>
              ) : (
                <div className="row g-2">
                  {availableUsers.map((u) => (
                    <div key={u.id} className="col-md-6 col-lg-4">
                      <div 
                        className={`card ${selectedUser === u.id ? 'border-primary shadow' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedUser(u.id)}
                      >
                        <div className="card-body">
                          <h6 className="card-title">
                            {u.surname && u.name 
                              ? `${u.name} ${u.surname}` 
                              : u.username}
                          </h6>
                          <p className="card-text text-muted mb-0">
                            {u.username}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Закрыть
            </button>
          </div>
        </div>
      </div>
    );
  }, [project, usersInProject, searchTerm, selectedUser, availableUsers, onClose, onAddUser, onRemoveUser]);

  return modalContent;
});

export default ProjectUsersModal;
